<template>
  <div class="max-w-5xl mx-auto">
    <!-- 页面标题 -->
    <div class="text-center mb-12">
      <h1 class="text-[clamp(1.5rem,3vw,2.5rem)] font-bold text-neutral-800 mb-4">常见问题解答</h1>
      <p class="text-neutral-500 max-w-2xl mx-auto">我们整理了用户最常问的问题，希望能快速解答您的疑惑</p>
    </div>

    <!-- 搜索框 -->
    <div class="mb-8">
      <div class="relative max-w-2xl mx-auto">
        <input 
          v-model="searchQuery" 
          type="text" 
          placeholder="搜索问题关键词..."
          class="w-full pl-12 pr-4 py-3 border border-neutral-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        >
        <i class="fa fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-neutral-400"></i>
      </div>
    </div>

    <!-- 问题分类标签 -->
    <div class="flex flex-wrap justify-center gap-2 mb-10">
      <button 
        v-for="category in categories" 
        :key="category.id"
        @click="activeCategory = category.id"
        :class="[
          'px-4 py-2 rounded-full text-sm transition-all',
          activeCategory === category.id 
            ? 'bg-primary text-white' 
            : 'bg-white text-neutral-700 hover:bg-neutral-100 border border-neutral-200'
        ]"
      >
        {{ category.name }}
      </button>
    </div>

    <!-- 常见问题列表 -->
    <div class="bg-white rounded-lg shadow-elevation-1 p-6 md:p-8">
      <div v-if="filteredFAQs.length === 0" class="text-center py-12">
        <i class="fa fa-search text-4xl text-neutral-300 mb-4"></i>
        <p class="text-neutral-500">未找到相关问题，请尝试其他关键词</p>
      </div>

      <div v-else class="space-y-4">
        <div 
          v-for="(faq, index) in filteredFAQs" 
          :key="index"
          class="border-b border-neutral-200 last:border-0 pb-4"
        >
          <button 
            @click="toggleFAQ(index)"
            class="flex justify-between items-start w-full text-left py-2 focus:outline-none"
          >
            <span class="font-medium text-neutral-800">{{ faq.question }}</span>
            <i 
              class="fa fa-chevron-down text-neutral-400 transition-transform"
              :class="{ 'rotate-180': openFAQs.includes(index) }"
            ></i>
          </button>
          
          <div 
            v-if="openFAQs.includes(index)"
            class="pl-4 mt-2 text-neutral-600 animate-fade-in"
          >
            <p class="mb-2">{{ faq.answer }}</p>
            
            <!-- 相关链接（如有） -->
            <div v-if="faq.link" class="mt-3">
              <router-link 
                :to="faq.link.to" 
                class="text-primary hover:underline inline-flex items-center text-sm"
              >
                {{ faq.link.text }} <i class="fa fa-angle-right ml-1"></i>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 仍有问题？联系我们 -->
    <div class="mt-12 bg-primary/5 rounded-lg p-6 text-center">
      <h2 class="text-xl font-semibold mb-4 text-neutral-800">仍有疑问？</h2>
      <p class="text-neutral-600 mb-6 max-w-2xl mx-auto">
        如果您的问题没有在上面找到答案，欢迎联系我们的客服团队，我们将竭诚为您解答
      </p>
      <router-link 
        to="/contact-us"
        class="inline-flex items-center bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-full transition-colors"
      >
        <i class="fa fa-headphones mr-2"></i> 联系客服
      </router-link>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FAQ',
  data() {
    return {
      // 搜索关键词
      searchQuery: '',
      // 当前选中的分类ID
      activeCategory: 'all',
      // 已展开的问题索引
      openFAQs: [],
      // 问题分类
      categories: [
        { id: 'all', name: '全部问题' },
        { id: 'account', name: '账户相关' },
        { id: 'order', name: '订单问题' },
        { id: 'payment', name: '支付相关' },
        { id: 'delivery', name: '配送物流' },
        { id: 'return', name: '退换退款' },
        { id: 'product', name: '商品咨询' },
        { id: 'membership', name: '会员服务' }
      ],
      // FAQ数据
      faqs: [
        // 账户相关
        {
          category: 'account',
          question: '如何注册EliteShop账号？',
          answer: '您可以点击网站右上角的"登录/注册"按钮，选择"注册"，输入手机号码并获取验证码，设置密码后即可完成注册。也可以使用微信、QQ等第三方账号快捷注册。',
          link: { text: '前往注册页面', to: '/register' }
        },
        {
          category: 'account',
          question: '忘记密码怎么办？',
          answer: '在登录页面点击"忘记密码"，输入您的注册手机号，获取验证码后即可重置密码。如果您绑定了邮箱，也可以通过邮箱验证重置密码。',
          link: { text: '找回密码', to: '/login#forgot' }
        },
        {
          category: 'account',
          question: '如何修改个人信息和绑定手机？',
          answer: '登录账号后，进入"我的账户-个人设置"页面，可以修改昵称、头像、收货地址等信息。修改绑定手机需要验证原手机号，如原手机号已停用，请联系客服协助处理。'
        },
        {
          category: 'account',
          question: '如何注销账号？',
          answer: '账号注销需要进入"我的账户-账户安全-注销账号"，阅读并同意注销协议后，完成身份验证即可提交注销申请。注销前请确保已处理完所有订单和资产，注销后账号信息将无法恢复。'
        },
        
        // 订单问题
        {
          category: 'order',
          question: '如何查询我的订单状态？',
          answer: '登录账号后，进入"我的订单"页面，可以查看所有订单的实时状态，包括待付款、待发货、已发货、已完成、已取消等。点击具体订单可查看详细物流信息。'
        },
        {
          category: 'order',
          question: '下单后可以修改订单信息吗？',
          answer: '订单提交后如未付款，可以在订单详情页点击"修改订单"调整收货地址、联系方式等信息。如已付款，请尽快联系客服协助处理，如商品尚未发货，一般可以修改；如已发货，则无法修改。'
        },
        {
          category: 'order',
          question: '如何取消订单？',
          answer: '未付款订单：可以直接在订单列表点击"取消订单"，选择取消原因即可。已付款订单：如商品尚未发货，可申请取消订单并退款；如商品已发货，需先拒收或签收后申请退货退款。'
        },
        {
          category: 'order',
          question: '订单显示付款成功但状态未更新怎么办？',
          answer: '这种情况通常是网络延迟导致的，您可以耐心等待5-10分钟后刷新页面查看。如仍未更新，请联系客服并提供付款凭证，我们会尽快核实处理。'
        },
        
        // 支付相关
        {
          category: 'payment',
          question: '支持哪些支付方式？',
          answer: '我们支持微信支付、支付宝、银联支付、信用卡支付、货到付款等多种支付方式。不同商品和地区支持的支付方式可能略有不同，具体以结算页面显示为准。'
        },
        {
          category: 'payment',
          question: '支付时提示支付失败怎么办？',
          answer: '1. 检查银行卡余额或支付账户余额是否充足；2. 确认支付密码/验证码输入正确；3. 检查网络连接是否正常；4. 如使用银行卡支付，确认银行卡未过期、未被冻结；5. 尝试更换支付方式或稍后再试。如问题持续，请联系支付机构或我们的客服。'
        },
        {
          category: 'payment',
          question: '支付成功但订单显示未付款？',
          answer: '这通常是支付渠道与平台对账延迟导致的，我们会在1-3个工作日内完成对账。如超过3个工作日仍未更新，请联系客服并提供以下信息：订单号、支付时间、支付金额、支付方式、付款账户信息（脱敏），我们会尽快核实处理。'
        },
        {
          category: 'payment',
          question: '可以开发票吗？如何申请？',
          answer: '可以开具电子发票或纸质发票。在下单时可以选择"需要发票"，填写发票抬头、税号等信息。电子发票会在订单完成后发送至您的邮箱，纸质发票会随商品一同寄出。如下单时未选择开发票，可在订单完成后30天内联系客服补开。'
        },
        
        // 配送物流
        {
          category: 'delivery',
          question: '配送范围和配送时间？',
          answer: '我们的商品覆盖全国大部分地区（港澳台除外），部分偏远地区可能无法配送，具体以结算页面显示为准。配送时间根据商品类型和收货地址不同而有所差异：1. 同城配送：1-2天；2. 省内配送：2-3天；3. 跨省配送：3-5天；4. 偏远地区：5-7天。'
        },
        {
          category: 'delivery',
          question: '如何查询物流信息？',
          answer: '订单发货后，您会收到短信通知，包含物流公司和运单号。您可以在"我的订单-订单详情"中查看实时物流信息，也可以复制运单号到物流公司官网查询。'
        },
        {
          category: 'delivery',
          question: '收货时发现商品破损怎么办？',
          answer: '如签收时发现商品破损，请当场拍照取证并拒收，同时联系客服说明情况。我们会为您安排补发或退款。如已签收后发现破损，请在24小时内联系客服并提供破损照片，我们会核实后为您处理。'
        },
        {
          category: 'delivery',
          question: '可以指定配送时间吗？',
          answer: '部分商品支持指定配送时间段，您可以在下单时选择期望的配送时间。如无指定时间选项，您可以在订单备注中注明，我们会尽量满足您的要求。生鲜、冷藏等特殊商品不支持指定配送时间。'
        },
        
        // 退换退款
        {
          category: 'return',
          question: '退换货政策是怎样的？',
          answer: '除特殊商品外，我们支持7天无理由退换货。商品需保持全新未使用、包装完好、配件齐全。食品、贴身用品、定制商品等不支持无理由退换货。质量问题退换货由我们承担运费，非质量问题退换货由您承担运费。',
          link: { text: '查看完整退款政策', to: '/refund-policy' }
        },
        {
          category: 'return',
          question: '如何申请退换货？',
          answer: '登录账号后，进入"我的订单-申请售后"，选择需要退换的订单，填写退换原因、上传相关照片，提交申请。客服会在24小时内审核，审核通过后您可将商品寄回指定地址，我们收到商品并验收后会为您办理退款或换货。'
        },
        {
          category: 'return',
          question: '退款会退到哪里？需要多久？',
          answer: '退款将原路返回至您的支付账户：1. 微信/支付宝支付：1-3个工作日；2. 银行卡支付：3-7个工作日；3. 货到付款：退回至您指定的银行卡/微信/支付宝，3-5个工作日。退款到账时间以银行处理为准。'
        },
        {
          category: 'return',
          question: '使用优惠券/礼品卡支付的订单退款时如何处理？',
          answer: '使用优惠券支付的部分，优惠券在有效期内将退回至您的账户，过期则不予补发；使用礼品卡支付的部分，将退回至您的礼品卡账户。现金支付部分将原路退回。'
        },
        
        // 商品咨询
        {
          category: 'product',
          question: '商品是正品吗？有质量保障吗？',
          answer: '我们承诺所有商品均为正品，假一赔三。所有商品均经过严格质检，符合国家相关标准。数码产品提供全国联保，家电产品按国家三包政策执行，其他商品提供7-15天质量保障期。'
        },
        {
          category: 'product',
          question: '商品价格会变动吗？有价格保护吗？',
          answer: '商品价格可能因促销活动、市场供需等因素调整。部分商品支持7天价格保护，如您购买后7天内商品降价，可在"我的订单"中申请价格补差。秒杀、团购、限时特惠等活动商品不支持价格保护。'
        },
        {
          category: 'product',
          question: '如何查看商品的生产日期/保质期？',
          answer: '食品、美妆、保健品等商品的生产日期/保质期信息会在商品详情页标注，您也可以在收到商品后查看商品包装。如对生产日期有特殊要求，请在下单前联系客服确认。'
        },
        {
          category: 'product',
          question: '商品保修政策是怎样的？',
          answer: '1. 数码产品：全国联保1年，部分品牌提供延保服务；2. 大家电：整机保修1年，核心部件保修3年；3. 小家电：保修6个月-1年；4. 其他商品：按国家相关规定执行。您可以在商品详情页查看具体保修政策，或联系品牌售后。'
        },
        
        // 会员服务
        {
          category: 'membership',
          question: '如何成为会员？会员有哪些权益？',
          answer: '注册账号并完成实名认证后自动成为普通会员，消费累计积分可升级为银卡、金卡、钻石会员。会员权益包括：积分抵扣现金、专享折扣、生日特权、免费退换货、专属客服、优先发货等，等级越高权益越多。'
        },
        {
          category: 'membership',
          question: '积分如何获取和使用？',
          answer: '1. 获取积分：购物消费（1元=1积分）、评价商品、每日签到、参与活动等；2. 使用积分：积分可在结算时抵扣现金（100积分=1元）、兑换优惠券、兑换商品、参与积分抽奖等。积分有效期为获取之日起12个月。'
        },
        {
          category: 'membership',
          question: '优惠券如何使用？有使用限制吗？',
          answer: '在结算页面可以选择使用优惠券，系统会自动抵扣相应金额。优惠券有使用条件限制，如：最低消费金额、适用商品范围、有效期等，具体以优惠券详情页显示为准。每张订单限用一张优惠券，优惠券不可叠加使用。'
        },
        {
          category: 'membership',
          question: '如何订阅促销信息？',
          answer: '您可以在"我的账户-消息设置"中开启促销信息推送，也可以关注我们的微信公众号、小程序、短信通知等渠道获取最新促销信息。您也可以随时在设置中关闭推送，退订短信回复"TD"即可。'
        }
      ]
    }
  },
  computed: {
    // 根据分类和搜索关键词筛选FAQ
    filteredFAQs() {
      // 如果选择全部分类，则显示所有FAQ
      let filtered = this.activeCategory === 'all' 
        ? this.faqs 
        : this.faqs.filter(faq => faq.category === this.activeCategory)
      
      // 如果有搜索关键词，进一步筛选
      if (this.searchQuery) {
        const keyword = this.searchQuery.toLowerCase()
        filtered = filtered.filter(faq => 
          faq.question.toLowerCase().includes(keyword) || 
          faq.answer.toLowerCase().includes(keyword)
        )
      }
      
      return filtered
    }
  },
  methods: {
    // 展开/折叠FAQ
    toggleFAQ(index) {
      if (this.openFAQs.includes(index)) {
        this.openFAQs = this.openFAQs.filter(i => i !== index)
      } else {
        this.openFAQs.push(index)
      }
    }
  }
}
</script>

<style scoped>
/* 渐入动画 */
.animate-fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>